// mysql value-slot adapter (T10, Docs/AutocompleteValueSlot-Plan.md) — mysql
// clone of the T8 pgsql exemplar. Turns the expression arm's existing column
// discovery into normalized ValueSlotColumnDescriptors for the shared policy
// (D10). CTE/derived projection goes through the SAME machinery the arm uses
// (`resolveCteColumns` / `inferSubqueryColumns`) — not reimplemented.
//
// Relation lookup mirrors the resolver's own private `resolveRelation` /
// `emitAliasColumns` semantics: an explicit database qualifier matches
// exactly, otherwise the first name match wins — mysql has no search_path,
// the bus snapshot only carries the current connection's database (see the
// "Schema vs. database" note atop `mysqlResolver.ts`).
//
// `@`/`@@` session variables are exposed via `extraScalarSources`, reusing
// the arm's own discovery (`findUserVariables` / `getMysqlSystemVariables`
// from `mysqlExtras.ts`) — NOT reimplemented. System variables stay gated
// behind `atPrefix === 'system'` (mirrors the arm's own gate at
// `mysqlResolver.ts` — the curated list is large).

import type { AliasBinding, CompletionCandidate, CteBinding, FkEdge } from '@/core/engines';
import type { ValueSlotInfo } from '@/features/sql-grammar/model';
import { inferSubqueryColumns } from '../resolver/inferSubqueryColumns';
import { resolveCteColumns } from '../resolver/resolveCteColumns';
import { resolveLhsInScope, type ScopedOwner } from '../valueSlot/scopedResolve';
import {
  coarseTypeOfDataType,
  isBooleanishDataType,
  type ValueSlotColumnDescriptor,
  type ValueSlotEngineAdapter,
  type ValueSlotRoutineDescriptor,
  type ValueSlotScope,
  type ValueSlotVirtualColumn,
} from '../valueSlot/types';
import { getMysqlSystemVariables } from './mysqlExtras';
import { foldIdentifier, formatIdentifier, type MysqlQuotingOptions } from './mysqlIdentifiers';

type AdapterTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly columns: ReadonlyArray<{ readonly name: string; readonly dataType: string }>;
};
type AdapterRoutine = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: string;
  readonly returns?: string;
};

/** Everything the adapter needs that is independent of the LHS operand. */
type MysqlAdapterSources = {
  readonly text: string;
  readonly aliasBindings: readonly AliasBinding[];
  readonly cteBindings: readonly CteBinding[];
  readonly snapshotTables: ReadonlyArray<AdapterTable>;
  readonly snapshotRoutines: ReadonlyArray<AdapterRoutine>;
  readonly snapshotForeignKeys: readonly FkEdge[];
  readonly quoting: MysqlQuotingOptions;
  /** Session user variables declared earlier in this buffer (`SET @x := …`)
   *  — the arm's `findUserVariables` buffer-wide scan, reused verbatim. */
  readonly userVars: readonly string[];
  /** `@@`-prefix detection at the cursor — mirrors the arm's own gate so
   *  the large system-variable list only surfaces once `@@` is typed. */
  readonly atPrefix: 'user' | 'system' | null;
  readonly mariadb: boolean;
  /** C2 P1-1 — resolver-supplied fallback for bindings whose relation is a
   *  VIRTUAL system table (information_schema/mysql system databases): a
   *  closure over the resolver's own static column map. Consulted only when
   *  the snapshot lookup yields nothing. */
  readonly virtualColumnsFor?: (binding: AliasBinding) => readonly ValueSlotVirtualColumn[] | undefined;
};

/** Scalar-value mode: a slot is REQUIRED. */
export type MysqlValueSlotContext = MysqlAdapterSources & {
  readonly slot: ValueSlotInfo;
};

/** Predicate-head mode (PH-D3, P1-sol F7): NO slot — a bare boolean-expression
 *  head has no LHS operand, so the scope resolves to
 *  `{ lhsType: 'unknown', lhsBooleanish: false }` and no column is ever
 *  flagged `fkPartnerOfLhs`. Declared as `slot?: undefined` rather than by
 *  omission so the overloads stay mutually exclusive: a caller passing a slot
 *  cannot land on this signature, and an existing scalar-value caller that
 *  DROPPED its slot fails to compile instead of silently degrading to
 *  no-LHS discovery. */
export type MysqlPredicateHeadContext = MysqlAdapterSources & {
  readonly slot?: undefined;
};

/** Implementation-side union — never the public parameter type. */
type MysqlAdapterContext = MysqlValueSlotContext | MysqlPredicateHeadContext;

/** Database-scoped relation lookup — parity with the resolver's private
 *  `resolveRelation` (explicit database qualifier → exact match; else the
 *  first name match — mysql has no search_path; the snapshot only carries
 *  the current connection's database). */
function lookupRelation(
  tables: ReadonlyArray<AdapterTable>,
  binding: AliasBinding,
  quoting: MysqlQuotingOptions,
): AdapterTable | undefined {
  const nameFolded = foldIdentifier(binding.relation, quoting);
  const dbFolded = binding.database
    ? foldIdentifier(binding.database, quoting)
    : binding.schema
      ? foldIdentifier(binding.schema, quoting)
      : null;
  if (dbFolded) {
    return tables.find(
      (t) => t.name.toLowerCase() === nameFolded && (t.database ?? '').toLowerCase() === dbFolded,
    );
  }
  return tables.find((t) => t.name.toLowerCase() === nameFolded);
}

function fkPartner(
  fks: readonly FkEdge[],
  lhs: { table: AdapterTable; column: string },
  cand: { table: AdapterTable; column: string },
): boolean {
  const match = (
    e: FkEdge,
    a: { table: AdapterTable; column: string },
    b: { table: AdapterTable; column: string },
  ) =>
    e.fromColumns.length === 1 &&
    e.fromTable.toLowerCase() === a.table.name.toLowerCase() &&
    e.fromColumns[0]!.toLowerCase() === a.column.toLowerCase() &&
    e.toTable.toLowerCase() === b.table.name.toLowerCase() &&
    e.toColumns[0]!.toLowerCase() === b.column.toLowerCase();
  return fks.some((e) => match(e, lhs, cand) || match(e, cand, lhs));
}

/** Resolve the slot's LHS to (owners, coarse type, boolean-ish) — the D5
 *  all-agree rule collapses disagreeing owners to 'unknown'. mysql's
 *  snapshot carries its namespace on `database`, not `schema` (see the
 *  resolver's own header note) — mapped into the shared resolver's `schema`
 *  slot so it lines up with the alias binding's own `schema`/`database`
 *  fallback chain (relationParser's generic dotted-qualifier parser lands a
 *  2-part `db.table` reference in `binding.schema`). */
function resolveLhs(args: MysqlAdapterContext): {
  owners: readonly ScopedOwner[];
  scope: ValueSlotScope;
} {
  // No slot (predicate-head mode) → no LHS → the neutral scope, via the same
  // path a slot with an unresolvable LHS already took.
  const lhs = args.slot?.lhs;
  if (!lhs) return { owners: [], scope: { lhsType: 'unknown', lhsBooleanish: false } };
  const owners = resolveLhsInScope({
    lhs,
    aliasBindings: args.aliasBindings,
    cteBindings: args.cteBindings,
    snapshotTables: args.snapshotTables.map((t) => ({
      name: t.name,
      schema: t.database ?? t.schema,
      kind: 'table' as const,
      columns: t.columns.map((c) => ({
        name: c.name, dataType: c.dataType, nullable: true, isPrimaryKey: false, ordinal: 0,
      })),
    })),
    source: args.text,
    engineId: 'mysql',
    fold: (s) => foldIdentifier(s, args.quoting),
  });
  const types = owners.map((o) =>
    coarseTypeOfDataType('dataType' in o.column ? o.column.dataType : undefined),
  );
  const allAgree = types.length > 0 && types.every((t) => t === types[0]);
  const bools = owners.map((o) =>
    isBooleanishDataType('dataType' in o.column ? o.column.dataType : undefined, 'mysql'),
  );
  return {
    owners,
    scope: {
      lhsType: allAgree ? types[0]! : 'unknown',
      lhsBooleanish: bools.length > 0 && bools.every(Boolean),
    },
  };
}

export function buildMysqlValueSlotAdapter(args: MysqlValueSlotContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildMysqlValueSlotAdapter(args: MysqlPredicateHeadContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
};
export function buildMysqlValueSlotAdapter(args: MysqlAdapterContext): {
  adapter: ValueSlotEngineAdapter;
  scope: ValueSlotScope;
} {
  const { owners, scope } = resolveLhs(args);
  const lhsOwner = owners.length === 1 ? owners[0] : undefined;
  const lhsTable =
    lhsOwner && (lhsOwner.binding.kind === 'table' || lhsOwner.binding.kind === 'view')
      ? lookupRelation(args.snapshotTables, lhsOwner.binding, args.quoting)
      : undefined;
  const lhsColumn = lhsOwner ? lhsOwner.column.name : undefined;

  const collectScopedColumns = (): readonly ValueSlotColumnDescriptor[] => {
    const out: ValueSlotColumnDescriptor[] = [];
    for (const b of args.aliasBindings) {
      const spelling = b.aliasSql ?? b.alias;
      const push = (name: string, dataType: string | undefined, table?: AdapterTable) => {
        out.push({
          label: name,
          insertBare: formatIdentifier(name),
          insertQualified: `${spelling}.${formatIdentifier(name)}`,
          owner: b,
          coarseType: coarseTypeOfDataType(dataType),
          booleanish: isBooleanishDataType(dataType, 'mysql'),
          fkPartnerOfLhs:
            table !== undefined &&
            lhsTable !== undefined &&
            lhsColumn !== undefined &&
            fkPartner(
              args.snapshotForeignKeys,
              { table: lhsTable, column: lhsColumn },
              { table, column: name },
            ),
        });
      };
      if (b.kind === 'subquery' || b.kind === 'derived') {
        const names =
          b.syntheticColumns && b.syntheticColumns.length > 0
            ? b.syntheticColumns
            : b.bodyRange
              ? inferSubqueryColumns({
                  source: args.text,
                  bodyRange: b.bodyRange,
                  engineId: 'mysql',
                  snapshotTables: args.snapshotTables,
                  cteBindings: args.cteBindings,
                })
              : [];
        for (const n of names) push(n, undefined);
        if (names.length === 0) {
          for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        }
        continue;
      }
      if (b.kind === 'cte') {
        const cte = args.cteBindings.find(
          (c) => foldIdentifier(c.name, args.quoting) === foldIdentifier(b.relation, args.quoting),
        );
        if (!cte) continue;
        const names = resolveCteColumns({
          cte,
          source: args.text,
          engineId: 'mysql',
          snapshotTables: args.snapshotTables,
          cteBindings: args.cteBindings,
        });
        for (const n of names) push(n, undefined);
        continue;
      }
      const tbl = lookupRelation(args.snapshotTables, b, args.quoting);
      if (!tbl) {
        for (const v of args.virtualColumnsFor?.(b) ?? []) push(v.name, v.dataType);
        continue;
      }
      for (const c of tbl.columns) push(c.name, c.dataType, tbl);
    }
    return out;
  };

  const collectScalarRoutines = (): readonly ValueSlotRoutineDescriptor[] =>
    collectMysqlScalarRoutineDescriptors(args.snapshotRoutines);

  // `@`/`@@` session variables — legal scalar values at a comparison RHS.
  // Reuses the arm's existing discovery verbatim: `findUserVariables`
  // (buffer-wide scan, always surfaced when present, matching the arm's
  // unconditional `if (args.userVars.length > 0) emitUserVariables(...)`)
  // and `getMysqlSystemVariables` (gated behind `atPrefix === 'system'`,
  // matching the arm's own `if (args.atPrefix === 'system') …` gate — the
  // curated list is large enough that unconditional emission would flood
  // the slot). The shared policy clamps both to tier ≥3.
  const extraScalarSources = (): readonly CompletionCandidate[] => {
    const out: CompletionCandidate[] = [];
    for (const name of args.userVars) {
      out.push({
        label: `@${name}`,
        insertText: name,
        isSnippet: false,
        kind: 'variable',
        tier: 3,
        detail: 'User variable',
        filterText: name,
      });
    }
    if (args.atPrefix === 'system') {
      for (const v of getMysqlSystemVariables({ mariadb: args.mariadb })) {
        out.push({
          label: `@@${v.name}`,
          insertText: v.name,
          isSnippet: false,
          kind: 'variable',
          tier: 3,
          detail: v.description ?? 'System variable',
          filterText: v.name,
        });
      }
    }
    return out;
  };

  return {
    adapter: {
      engine: 'mysql',
      collectScopedColumns,
      collectScalarRoutines,
      extraScalarSources,
    },
    scope,
  };
}

/** Scalar-routine descriptor mapping shared by this adapter's
 *  `collectScalarRoutines` closure and the resolver's T18b typed-VALUES-
 *  element call site (`emitTypedValuesElement` in `mysqlResolver.ts`) — kept
 *  in one place so the two can't drift. */
export function collectMysqlScalarRoutineDescriptors(
  snapshotRoutines: ReadonlyArray<AdapterRoutine>,
): readonly ValueSlotRoutineDescriptor[] {
  return snapshotRoutines
    .filter((r) => r.kind !== 'procedure')
    .map((r) => ({
      label: r.name,
      insertText: `${r.name}($0)`,
      returnsCoarse: coarseTypeOfDataType(r.returns),
      returnsRaw: r.returns,
      // PH-D4 (P1-sol F6) — read only by the predicate-head policy, where a
      // boolean return promotes to T3. `coarseTypeOfDataType` maps
      // `tinyint(1)` to 'integer', so without this a tinyint(1)-returning
      // routine would never promote at a head. Harmless to the value-slot
      // policy, which ignores the field.
      returnsBooleanish: isBooleanishDataType(r.returns, 'mysql'),
    }));
}
